import { z } from "zod";

/**
 * Enumeration defining user roles and their permission levels.
 * Determines access rights and capabilities within the application.
 */
export const UserRole = {
  OWNER: "owner",
  COMPANY_ADMIN: "company_admin",
  COMPANY_MANAGER: "company_manager",
} as const;

export type UserRoleType = (typeof UserRole)[keyof typeof UserRole];

export const CompanyPaymentPlan = {
  FREE: "free",
  START: "start",
  PRO: "pro",
  PRO_PLUS: "pro_plus",
  ENTERPRISE: "enterprise",
  CUSTOM: "custom",
} as const;

export type CompanyPaymentPlanType =
  (typeof CompanyPaymentPlan)[keyof typeof CompanyPaymentPlan];

export const CompanyPaymentPeriod = {
  YEARLY: "yearly",
  MONTHLY: "monthly",
} as const;

export type CompanyPaymentPeriodType =
  (typeof CompanyPaymentPeriod)[keyof typeof CompanyPaymentPeriod];

export const authTokensResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type AuthTokensResponse = z.infer<typeof authTokensResponseSchema>;

export const meResponseSchema = z.object({
  name: z.string(),
  surname: z.string(),
  email: z.string(),
  role: z.enum([
    UserRole.COMPANY_ADMIN,
    UserRole.COMPANY_MANAGER,
    UserRole.OWNER,
  ]),
  companyId: z.number(),
  companyDemoDeadline: z.string().nullable(),
  companyName: z.string(),
  companyPaymentPlan: z.enum([
    CompanyPaymentPlan.FREE,
    CompanyPaymentPlan.START,
    CompanyPaymentPlan.PRO,
    CompanyPaymentPlan.PRO_PLUS,
    CompanyPaymentPlan.ENTERPRISE,
    CompanyPaymentPlan.CUSTOM,
  ]),
  companyPaymentPeriod: z.enum([
    CompanyPaymentPeriod.YEARLY,
    CompanyPaymentPeriod.MONTHLY,
  ]),
  companyApplicationsCount: z.number(),
  companyApplicationsLimit: z.number(),
  adminAsForCompanyId: z.number().nullable(),
  isCompanyBlocked: z.boolean(),
  isCompanyBlockedByDemoExpiration: z.boolean(),
  isCompanyTelegramConnected: z.boolean(),
  isCompanyNew: z.boolean(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * Allowed payment plans for signup.
 * Only plans with demo period are available during registration.
 */
export const SIGNUP_ALLOWED_PLANS = [
  CompanyPaymentPlan.START,
  CompanyPaymentPlan.PRO,
  CompanyPaymentPlan.PRO_PLUS,
] as const;

export type SignupAllowedPlan = (typeof SIGNUP_ALLOWED_PLANS)[number];

export const signUpSchema = z
  .object({
    name: z.string().min(1, "Імʼя є обовʼязковим"),
    surname: z.string().min(1, "Прізвище є обовʼязковою"),
    companyName: z.string().min(1, "Назва компанії є обовʼязковим"),
    email: z.string().email("Невірний формат електронної пошти"),
    password: z.string().min(6, "Пароль повинен містити щонайменше 6 символів"),
    passwordConfirm: z.string().min(1, "Підтвердження паролю є обовʼязковим"),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Паролі не співпадають",
    path: ["passwordConfirm"],
  });

export type SignUpFormData = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({
  email: z.string().email("Невірний формат електронної пошти"),
  password: z.string().min(1, "Пароль є обовʼязковим"),
});

export type SignInFormData = z.infer<typeof signInSchema>;

export const acceptInvitationSchema = z
  .object({
    password: z.string().min(6, "Пароль повинен містити щонайменше 6 символів"),
    passwordConfirm: z.string().min(1, "Підтвердження паролю є обовʼязковим"),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Паролі не співпадають",
    path: ["passwordConfirm"],
  });

export type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;
